import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { CustomerService } from '../api/api_pb';
import { useClient } from '../connect_api';
import { Loader2 } from 'lucide-react';

interface SendSMSModalProps {
    isOpen: boolean;
    onClose: () => void;
    customerUuid: string;
    mobileNumber: string;
}

const MAX_MESSAGE_LENGTH = 1600;
const CHARS_PER_SEGMENT = 160;

const SendSMSModal: React.FC<SendSMSModalProps> = ({ isOpen, onClose, customerUuid, mobileNumber }) => {
    const client = useClient(CustomerService);
    const [message, setMessage] = useState('');
    const [isSending, setIsSending] = useState(false);

    const segmentCount = message.length > 0 ? Math.ceil(message.length / CHARS_PER_SEGMENT) : 0;

    const handleClose = () => {
        setMessage('');
        onClose();
    };

    const handleSend = async () => {
        if (!message.trim()) {
            toast.error('Message cannot be empty');
            return;
        }

        setIsSending(true);
        try {
            const response = await client.sendSMSToCustomer({
                customerUuid,
                message: message.trim(),
            });

            if (response.success) {
                toast.success('SMS sent successfully');
                setMessage('');
                onClose();
            } else {
                toast.error(response.errorMessage || 'Failed to send SMS');
            }
        } catch (error: any) {
            toast.error(`Failed to send SMS: ${error.message}`);
        } finally {
            setIsSending(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-11/12 max-w-md">
                <h2 className="text-lg font-semibold mb-4">Send SMS</h2>

                <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
                <input
                    type="text"
                    value={mobileNumber}
                    readOnly
                    className="border border-gray-300 p-2 w-full mb-4 rounded-md bg-gray-50 text-gray-600"
                />

                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
                    className="border border-gray-300 p-2 w-full mb-2 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                    rows={4}
                    placeholder="Type your message..."
                    disabled={isSending}
                    maxLength={MAX_MESSAGE_LENGTH}
                />
                <p className="text-sm text-gray-500 mb-4">
                    {message.length}/{MAX_MESSAGE_LENGTH} characters ({segmentCount} {segmentCount === 1 ? 'segment' : 'segments'})
                </p>

                <div className="flex justify-end gap-2">
                    <button
                        onClick={handleClose}
                        className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
                        disabled={isSending}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSend}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        disabled={isSending || !message.trim()}
                    >
                        {isSending ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Sending...
                            </>
                        ) : (
                            'Send SMS'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SendSMSModal;
